import { UseFormGetValues } from "react-hook-form";
import { format, parse } from "date-fns";

import { DEFAULT_DIRECTION } from "src/constants";

import { UserType } from "src/types/users";
import { CaseCloneResponseType } from "src/pages/cloneCase/types";
import { 
  AddCaseProps, CaseDoctorProps, InsuranceAttributesProps, PatientAddress, PatientAttributes, 
  PatientPhoneNumber, PrescriptionAttributesType, ProcedureProps, ShipmentAttributesProps
} from "src/pages/addCase/types";



export function populatingCloneCase({ data }: CaseCloneResponseType, userData: UserType, getValues:  UseFormGetValues<AddCaseProps>): AddCaseProps {
  const caseFormData: AddCaseProps = {} as AddCaseProps;

  if (data.case) {
    caseFormData.mos = data.case.mos || getValues("mos");
    caseFormData.user_id = userData ? userData.id : 0;
    caseFormData.creator_name = userData ? userData.name : '';
    caseFormData.submission_source = data.case.submission_source || "provider";
    caseFormData.category = data.case.category || '';
    
    caseFormData.prescription_attributes = {} as PrescriptionAttributesType;
    caseFormData.prescription_attributes.drug_name = data.case.drug_name;
    caseFormData.prescription_attributes.drug_id = data.case.drug_id || getValues("prescription_attributes.drug_id");
  }

  caseFormData.procedure_attributes = {}  as ProcedureProps;
  if (data.procedure) {
    caseFormData.procedure_attributes.cpt_code = data.procedure[0].cpt_code;
    caseFormData.procedure_attributes.secondary_cpt_code = data.procedure && data.procedure[1] ? data.procedure[1].cpt_code : "";
  }

  caseFormData.shipments_attributes = [{} as ShipmentAttributesProps]
  
  if (data.soc) {
    if (data.soc.length > 0) {
      if (data.soc[0].value === "0")
      caseFormData.shipments_attributes[0].practice_id = Number(data.soc[0].value);
    }
  }

  if (data.shipments && data.shipments.length > 0) {
    const { practice_id, practice_name, practice_type, ship_to_street, ship_to_suite, ship_to_city, ship_to_state, ship_to_zip } = data.shipments[0];

    caseFormData.shipments_attributes[0].practice_id = practice_id;
    caseFormData.shipments_attributes[0].practice_name = practice_name;
    caseFormData.shipments_attributes[0].practice_type = practice_type;
    caseFormData.shipments_attributes[0].ship_to_street = ship_to_street;
    caseFormData.shipments_attributes[0].ship_to_suite = ship_to_suite || "";
    caseFormData.shipments_attributes[0].ship_to_city = ship_to_city;
    caseFormData.shipments_attributes[0].ship_to_state = ship_to_state;
    caseFormData.shipments_attributes[0].ship_to_zip = ship_to_zip;
  }

  if (data.shipment_attributes && data.shipment_attributes.value) {
    caseFormData.shipments_attributes[0].ship_to_npi = data.shipment_attributes.value;
  }

  caseFormData.prescription_attributes = {} as PrescriptionAttributesType;

  if (data.prescription) {
    const diagnosis = data.prescription.diagnosis_code;
    caseFormData.prescription_attributes.diagnosis_code = diagnosis !== "" ? diagnosis.split(",") : [];
    caseFormData.prescription_attributes.note = data.prescription.note || "";
    caseFormData.prescription_attributes.direction = DEFAULT_DIRECTION;

    const drug_name = data.prescription.product;
    caseFormData.prescription_attributes.drug_name = drug_name;
  }

  if (data.prescription) {
    caseFormData.prescription_attributes.drug_id = data.prescription.drug_id;
    caseFormData.prescription_attributes.qty = Number(data.prescription.qty) || 0;
  }

  if (data.prescription_attributes.length > 0) {
    data.prescription_attributes.forEach((attr) => {
      switch(attr.name) {
        case "treatment_date":
          caseFormData.prescription_attributes.treatment_date = attr.value || "";
          break;
        case "surgery":
          caseFormData.prescription_attributes.surgery = attr.value || "";
      }
    })
  }

  if (data.doctor) {
    caseFormData.doctor_firstlastname = `${data.doctor.firstname} ${data.doctor.lastname}`;
    caseFormData.doctor_fullname =
      `${data.doctor.firstname} ${data.doctor.middlename || ""} ${data.doctor.lastname}`;

    caseFormData.doctor_attributes = {
      practice_id: data.doctor.practice_id,
      global_doctor_id: data.doctor.global_doctor_id,
    } as CaseDoctorProps;
  }

  if (data.practice || data.practice_address) {
    caseFormData.practice_name = data.practice.name;
  }

  if (data.patient) {
    const { patient } = data;

    caseFormData.patient_firstlastname = `${patient.firstname} ${patient.lastname}`;
    caseFormData.patient_fullname = `${patient.firstname} ${patient.middlename || ""} ${patient.lastname}`;
    caseFormData.patient_dob = patient.dob;

    caseFormData.patient_attributes = {
      global_patient_id: patient.global_patient_id,
      title: patient.title || "",
      firstname: patient.firstname,
      middlename: patient.middlename || "",
      lastname: patient.lastname,
      gender: patient.gender,
      dob: format(parse(patient.dob, "yyyyy-MM-dd", new Date()), "MM/dd/yyyy"),
      ssn: patient.ssn ? patient.ssn : undefined,
      best_time_to_contact: patient.best_time_to_contact || "",
      patient_email_id: null,
      patient_email: data.email_address ? data.email_address.email : "",
    } as PatientAttributes;
  }

  if (data.patient_phone_numbers && data.patient_phone_numbers.length > 0) {
    caseFormData.patient_attributes.phone_numbers = [];

    data.patient_phone_numbers.forEach((phone, index) => {
      caseFormData.patient_attributes.phone_numbers[index] = {} as PatientPhoneNumber;
      caseFormData.patient_attributes.phone_numbers[index].number = phone.number.toString();
      caseFormData.patient_attributes.phone_numbers[index].phone_type = phone.phone_type || "cell";
      caseFormData.patient_attributes.phone_numbers[index].is_ok_to_leave_message = phone.is_ok_to_leave_message;
      caseFormData.patient_attributes.phone_numbers[index].is_preferred = phone.is_preferred;
      caseFormData.patient_attributes.phone_numbers[index].id = phone.phone_number_id;
    });
  }

  caseFormData.patient_attributes.address = {
    address_name: "home",
  } as PatientAddress;
  if (data.patient_address) {
    caseFormData.patient_attributes.address.street = data.patient_address.street || "";
    caseFormData.patient_attributes.address.suite = data.patient_address.suite || "";
    caseFormData.patient_attributes.address.city = data.patient_address.city || "";
    caseFormData.patient_attributes.address.state = data.patient_address.state || "";
    caseFormData.patient_attributes.address.zip = data.patient_address.zip || "";
  }

  if (data.patient_entities_address) {
    caseFormData.patient_attributes.address.address_name = data.patient_entities_address.address_name || "";
  }

  caseFormData.insurances_attributes = [
    {} as InsuranceAttributesProps,
  ];
  if (data.insurances && data.insurances.length > 0) {
    data.insurances.forEach((insurance, index) => {
      if (caseFormData.insurances_attributes) {
        caseFormData.insurances_attributes[index] = {} as InsuranceAttributesProps;
        caseFormData.insurances_attributes[index].insurance_name = insurance.insurance_name;
        caseFormData.insurances_attributes[index].relation_to_patient = insurance.relation_to_patient;
        caseFormData.insurances_attributes[index].relation_firstname = insurance.relation_firstname || "";
        caseFormData.insurances_attributes[index].relation_middlename = insurance.relation_middlename || "";
        caseFormData.insurances_attributes[index].relation_lastname = insurance.relation_lastname || "";
        caseFormData.insurances_attributes[index].policy = insurance.policy || "";
        caseFormData.insurances_attributes[index].group_number = insurance.group_number || "";
        caseFormData.insurances_attributes[index].insurance_number = insurance.insurance_number || "";
        caseFormData.insurances_attributes[index].member_id = insurance.member_id || "";
        caseFormData.insurances_attributes[index].bin = insurance.bin || "";
        caseFormData.insurances_attributes[index].pcn = insurance.pcn || "";
        caseFormData.insurances_attributes[index].rx_group = insurance.rx_group || "";
        caseFormData.insurances_attributes[index].global_insurance_id = insurance.global_insurance_id ? insurance.global_insurance_id.toString() : null;
        caseFormData.insurances_attributes[index].is_primary_insurance = index === 0;
        caseFormData.insurances_attributes[index].insurance_priority = index;
      }
    });
  }

  return caseFormData;
}
